import Backbone from 'backbone';
import $ from 'jquery';
import {debounce, DebouncedFunction} from 'ts-debounce';

import UserConstants from 'constants/UserConstants';

const STATUS_SHARED_CLASS = 'fa';
const STATUS_ERROR_CLASS = 'fa-times u-danger-color';
const STATUS_SUCCESS_CLASS = 'fa-check u-success-color';
const FIELDS_TO_TRIM = ['username', 'email', 'name'];

class GettingStartedView extends Backbone.View<Backbone.Model> {
  debouncedAuthenticateField!: DebouncedFunction<(e: JQueryEventObject) => void>;

  static initClass(): void {
    this.prototype.events = {
      // @ts-expect-error: Backbone events can be either the events hash or a method returning the events hash
      // The Backbone types do not know about this.
      'change select[name="affiliation"]': 'toggleFields',
      'input input': 'delayedAuthenticateField',
      submit: 'disableSubmit',

      // We can't just do this on 'change input', since that gets called even before any user interaction.
      'blur input': 'handleBlur',
      'blur select': 'handleBlur',
      'change select': 'handleBlur',
    };
  }

  override initialize(): void {
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.debouncedAuthenticateField = debounce(this.authenticateField, 500);
  }

  getType(): string {
    return this.$('select[name="affiliation"]').val();
  }

  affiliationChangeHandler(type: string): void {
    this.$('.info-col .info').addClass('hidden');
    switch (type) {
      case UserConstants.PROCLAIMED_ACADEMIC:
        this.$('.info-col .academic-info').removeClass('hidden');
        break;
      case UserConstants.PROCLAIMED_COMMERCIAL:
        this.$('.info-col .commercial-info').removeClass('hidden');
        break;
      case UserConstants.PROCLAIMED_NONPROFIT:
        this.$('.info-col .nonprofit-info').removeClass('hidden');
        break;
      default:
        this.$('.info-col .general-info').removeClass('hidden');
    }
  }

  disableSubmit(e: JQueryEventObject): void {
    // @ts-expect-error You can totally send `true` to JQuery attrs, it just makes it stringy
    this.$('button[type="submit"]').attr('disabled', true).addClass('disabled');
  }

  delayedAuthenticateField(e: JQueryEventObject): void {
    // We update the status when the user is typing in the field AND the status is already showing. The
    // 500ms debounce just feels more natural.
    const field = e.target.id;
    const $statusField = $(`#${field}`).prevAll('.fieldStatus').find('.fieldStatus-icon');
    if ($statusField.hasClass('hidden')) {
      return;
    }
    $statusField
      .removeClass(STATUS_SHARED_CLASS)
      .removeClass(STATUS_ERROR_CLASS)
      .removeClass(STATUS_SUCCESS_CLASS)
      .addClass('spinner');
    this.debouncedAuthenticateField(e);
  }

  handleBlur(e: JQueryEventObject): void {
    const field = e.target.id;
    if (Array.from(FIELDS_TO_TRIM).includes(field)) {
      // @ts-expect-error `JQueryEventObject.target` is the narrow `Element`; this is in fact an `input` and has
      // a `value`
      e.currentTarget.value = e.currentTarget.value.trim();
    }

    // @ts-expect-error `JQueryEventObject.target` is the narrow `Element`; this is in fact an `input` and has
    // a `value`
    if (field === 'username' && e.currentTarget.value.length === 0) {
      // If the user clicks on an FAQ question before they type anything, we don't want to show
      // the error notification. In the worst case, the user will skip the username field and fill in
      // everything else, and get the error notification after clicking Create Account (but this seems
      // extremely unlikely).
      return;
    }

    this.authenticateField(e);
  }

  authenticateField(e: JQueryEventObject): void {
    const field = e.target.id;
    this.authenticateForm().done((errors) => {
      const $statusField = $(`#${field}`).prevAll('.fieldStatus').find('.fieldStatus-icon');
      const $errorField = $(`#${field} ~ .legacy-field-error`);
      const isError = errors[field] != null;
      $errorField.text(isError ? errors[field][0] : '').toggleClass('hidden', !isError);
      $statusField
        .addClass(STATUS_SHARED_CLASS)
        .toggleClass(STATUS_ERROR_CLASS, isError)
        .toggleClass(STATUS_SUCCESS_CLASS, !isError)
        .removeClass('hidden spinner');
    });
  }

  authenticateForm(): JQueryXHR {
    const formData = new FormData();
    this.$('input, select').each((i, field) => {
      let value = $(field).val();
      if (Array.from(FIELDS_TO_TRIM).includes(field.id)) {
        value = value.trim();
      }
      formData.append($(field).attr('name'), value);
    });
    const url = this.$('form').data('validate-url');
    return $.ajax({
      url: url != null ? url : '/1/api/auth/validate-signup',
      data: formData,
      processData: false,
      contentType: false,
      type: 'POST',
    });
  }

  toggleFields(): void {
    const type = this.getType();
    const isAcademic = type === UserConstants.PROCLAIMED_ACADEMIC;
    const isCommercial = type !== '' && !isAcademic;
    this.$('.js-academic-email-description').toggleClass('hidden', !isAcademic);
    this.$('.academic-fields').toggleClass('hidden', !isAcademic);
    this.$('.commercial-fields').toggleClass('hidden', !isCommercial);
    if (typeof this.affiliationChangeHandler === 'function') {
      this.affiliationChangeHandler(type);
    }
  }

  override render(): this {
    this.toggleFields();
    return this;
  }
}
GettingStartedView.initClass();

export default GettingStartedView;
